import React from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';

/**
 * Unified rich-text renderer for ALL AI-generated content (Homepage AI,
 * AI Navigator, Practitioner Assistant, Deep Analysis, etc.).
 *
 * Single source of truth so paragraph spacing, emphasis, lists, unicode
 * symbols (O₁/O₂), and Chinese/English mixed typography render identically
 * everywhere.
 */
interface AIMarkdownProps {
  content: string;
  className?: string;
}

// Lightweight sanitizer shared across surfaces – strips control markers /
// JSON trailers the model occasionally leaks. Falls back to raw text.
export const sanitizeAIContent = (raw: string): string => {
  if (!raw) return '';
  try {
    let out = raw;
    out = out.replace(/\n?\{"phase":"[^"]+"\}\s*$/g, '');
    out = out.replace(/\n?\{"analysis_complete":[\s\S]*?\}\s*$/g, '');
    out = out.replace(/\n?\{"root_cause_complete":[\s\S]*?\}\s*$/g, '');
    out = out.replace(/\n?\{"therapy_recommendations":[\s\S]*?\}\s*$/g, '');
    out = out.replace(
      /\[\s*(quick_reply|stage_name|analysis_phase|prompt_scaffolding|hidden|control|meta|internal)\s*:\s*[\s\S]*?\]/gi,
      ''
    );
    out = out.replace(/\{\{\s*[a-z_]+\s*\}\}/gi, '');
    out = out.replace(
      /^\s*(stage\s*name|analysis\s*phase|prompt\s*scaffolding|hidden\s*control(?:\s*text)?)\s*[:：].*$/gim,
      ''
    );
    out = out.replace(/\n{3,}/g, '\n\n').trim();
    return out || raw;
  } catch {
    return raw;
  }
};

const components: Components = {
  p: ({ children }) => <p className="my-2 leading-relaxed">{children}</p>,
  strong: ({ children }) => (
    <strong className="font-semibold text-amber-800">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="my-2 ml-5 list-disc space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="my-2 ml-5 list-decimal space-y-1">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="my-2 border-l-2 border-amber-300 pl-3 italic text-foreground/80">
      {children}
    </blockquote>
  ),
  h1: ({ children }) => <h3 className="mt-3 mb-2 text-base font-semibold">{children}</h3>,
  h2: ({ children }) => <h4 className="mt-3 mb-2 text-base font-semibold">{children}</h4>,
  h3: ({ children }) => <h4 className="mt-3 mb-2 text-sm font-semibold">{children}</h4>,
  code: ({ children }) => (
    <code className="rounded bg-amber-50 px-1 py-0.5 font-mono text-[0.9em]">{children}</code>
  ),
  a: ({ children, href }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-amber-700 underline underline-offset-2">
      {children}
    </a>
  ),
};

const AIMarkdown: React.FC<AIMarkdownProps> = ({ content, className }) => {
  const clean = sanitizeAIContent(content);
  return (
    <div
      className={cn(
        // Font-stack with fallbacks for subscript/unicode/CJK glyphs
        "ai-markdown text-sm text-foreground break-words",
        "[font-feature-settings:'kern','liga','clig','calt']",
        "[font-family:Inter,'PingFang_SC','Hiragino_Sans_GB','Microsoft_YaHei','Noto_Sans_CJK_SC','Noto_Sans',system-ui,sans-serif]",
        className
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {clean}
      </ReactMarkdown>
    </div>
  );
};

export default AIMarkdown;
